<script setup lang="ts">
import { useHead } from '@unhead/vue'
import { storeToRefs } from 'pinia'
import { computed, defineEmits, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useDisplay } from 'vuetify'

import { useTrustedShops } from '@/composables/useTrustedShopCard'
import { API_URL } from '@/constants/GlobalConstants'
import { translate } from '@/plugins/i18n'
import ApiController from '@/services/ApiController'
import { WALKER_COMMANDS } from '@/services/helpers/ElbwalkerHelper'
import { NO_INDEX_FOLLOW } from '@/services/helpers/MetaHelper'
import TranslationsHelper from '@/services/helpers/TranslationsHelper'
import UtilityHelper from '@/services/helpers/UtilityHelper'
import SiteConfig from '@/services/SiteConfig'
import { elb } from '@elbwalker/walker.js'

import logoMain from '@/assets/images/logo-main.svg'
import LoadingOverlay from '@/components/basic/atoms/LoadingOverlay.vue'
import TrustedShopsCard from '@/components/basic/molecules/TrustedShopsCard.vue'
import FocusPlusCampaingBanner from '@/components/checkout/atoms/FocusPlusCampaignBanner.vue'
import CheckoutRegisterCustomer from '@/components/checkout/molecules/CheckoutRegisterCustomer.vue'
import CheckoutUpsell from '@/components/checkout/molecules/CheckoutUpsell.vue'
import SuccessHeader from '@/components/checkout/molecules/SuccessHeader.vue'
import { useCustomerStore } from '@/stores/customer'
import { useNewCheckoutStore } from '@/stores/newCheckout'

useHead({
  title: translate('checkout:order_success_title'),
  meta: [{ name: 'robots', content: NO_INDEX_FOLLOW }]
})

const customerStore = useCustomerStore()
const checkoutStore = useNewCheckoutStore()
const { profile } = storeToRefs(customerStore)

const CREDITS_AVAILABLE_WITHIN_DAYS = 21

const route = useRoute()
const display = useDisplay()

const IMG_DIMENSIONS = { width: 500, height: 550 }
const locale = SiteConfig.locale
const TS_ID = {
  de: import.meta.env.VITE_TRUSTED_SHOPS_ID_VIC_DE,
  at: import.meta.env.VITE_TRUSTED_SHOPS_ID_VIC_AT,
  ch: import.meta.env.VITE_TRUSTED_SHOPS_ID_VIC_CH
}[locale]

const hash = computed(() => route.params.h)
const placeholderImage = new URL('@/assets/images/bottle.svg', import.meta.url)
  .href
const isLoading = ref(true)
const isUpsellActive = ref(false)
const elbwalkerTarget = ref(null)
const successData = ref(null)
const elbOrderData = ref('')
const { data: trustedShopData, reviewLink } = useTrustedShops(TS_ID)

const emit = defineEmits(['update:loading'])

const getElbwalkerSessionId = () => {
  try {
    const sessionData = localStorage.getItem('elbSessionId')
    if (sessionData) {
      const parsed = JSON.parse(sessionData)
      const vData = JSON.parse(parsed.v)
      return vData.id
    }
    return null
  } catch (error) {
    console.error('Failed to parse session data:', error)
    return null
  }
}

const pushPurchaseDataToGtm = async (data: any) => {
  try {
    const [{ default: SiteConfig }, { default: TagManager }] =
      await Promise.all([
        import('@/services/SiteConfig'),
        import('@/services/analytics/TagManager')
      ])

    const { order_increment_id, order_details } = data

    if (!order_details) {
      console.warn('No order details found, cannot track purchase event')
      return
    }

    const orderData = {
      ecommerce: {
        purchase: {
          actionField: {
            id: order_increment_id,
            affiliation: SiteConfig.name,
            revenue: order_details?.total_amount,
            tax: order_details?.tax_total,
            shipping: order_details?.shipping_total,
            coupon: order_details?.coupon_code,
            firstOrder: order_details?.is_first_order
          }
        },
        upsell: order_details?.is_upsell,
        sessionId: getElbwalkerSessionId(),
        timestamp: Date.now(),
        netRevenue: order_details?.net_amount,
        currencyCode: order_details?.currency,
        opc: order_details?.is_opc,
        itemsQtyCount: order_details?.bottle_count,
        orderPaymentCode: order_details?.payment_type
      }
    }

    TagManager.pushEvent('order_data', orderData)
  } catch (error) {
    console.error('Failed to track purchase event:', error)
  }
}

const cashbackText = computed(() => {
  //@ts-ignore
  const { value } = successData.value?.cashback || {}
  return value
    ? translate(
        'checkout:credits_info',
        TranslationsHelper.formatCurrency(value.toFixed(2)),
        CREDITS_AVAILABLE_WITHIN_DAYS
      )
    : ''
})

const canSetPassword = computed(() => {
  //@ts-ignore
  return successData.value?.can_set_password && profile.value?.email
})

const showFocusPlusCampaignBanner = computed(() => {
  //@ts-ignore
  return successData.value?.show_focus_plus_text
})
const isDataReady = computed(() => !isLoading.value && !!elbOrderData.value)

const isGuestOrder = computed(() => {
  //@ts-ignore
  return successData.value?.is_guest === true
})

const buildImageUrl = (productId: string, campaignId: string) => {
  const type = display.smAndDown.value ? 'small_image' : 'image'
  const density = window.devicePixelRatio ?? 1
  const appendix = `?type=${type}&height=${IMG_DIMENSIONS.height * density}&width=${IMG_DIMENSIONS.width * density}`
  return UtilityHelper.formatString(
    API_URL.CATALOG.PRODUCT.GET_V2_IMAGE,
    productId,
    campaignId,
    appendix
  )
}

const productImage = computed(() => {
  //@ts-ignore
  const upsell = successData.value?.upsell
  return isUpsellActive.value && upsell
    ? buildImageUrl(upsell.condition_product_id, upsell.condition_campaign_id)
    : null
})

const upsellImage = computed(() => {
  //@ts-ignore
  const upsell = successData.value?.upsell
  return isUpsellActive.value && upsell
    ? buildImageUrl(upsell.upsell_product_id, upsell.upsell_campaign_id)
    : null
})

async function loadSuccessData(hash: string) {
  const result = await ApiController.fetchSuccess(hash)
  const data = result.data
  successData.value = data

  if (!data) {
    isUpsellActive.value = false
    return
  }

  const { order_increment_id, order_details, upsell, cashback } = data

  await pushPurchaseDataToGtm(data)

  elbOrderData.value = [
    'checkout_version:2',
    `order_increment_id:${order_increment_id}`,
    `currency:${order_details?.currency}`,
    `est_delivery_date:${order_details?.est_delivery_date}`,
    `payment_type:${order_details?.payment_type}`,
    `grand_total:${order_details?.total_amount}`,
    `coupon_code:${order_details?.coupon_code}`,
    `coupon_discount_amount:${order_details?.coupon_discount_amount}`,
    `used_credits_amount:${order_details?.used_credits_amount}`,
    `cashback_amount:${cashback?.value}`,
    `bottle_count:${order_details?.bottle_count}`,
    `shipping_total:${order_details?.shipping_total}`,
    `tax_total:${order_details?.tax_total}`,
    `vendor_count:${order_details?.vendor_count}`
  ].join(';')

  Object.assign(trustedShopData.value, {
    orderNr: String(order_increment_id),
    email: String(profile.value?.email || ''),
    amount: String(order_details?.total_amount),
    currency: String(order_details?.currency || 'EUR'),
    paymentType: String(order_details?.payment_type),
    estDeliveryDate: String(order_details?.est_delivery_date)
  })

  isUpsellActive.value = !!(upsell && Object.keys(upsell).length > 0)
}

watch(
  () => hash.value,
  async (newHash) => {
    if (!newHash) return

    emit('update:loading', true)
    isLoading.value = true

    try {
      //@ts-ignore
      await loadSuccessData(newHash)
    } catch (err) {
      console.error('Failed to fetch success data:', err)
    } finally {
      isLoading.value = false
      emit('update:loading', false)
    }
  },
  { immediate: true }
)

watch(isDataReady, async (ready) => {
  if (!ready) return
  await nextTick()
  const el = elbwalkerTarget.value
  if (el) {
    elb(WALKER_COMMANDS.run)
  } else {
    console.warn('[Elbwalker] DOM element not found')
  }
})

const successContainer = ref(null)

onMounted(() => {
  successContainer.value?.el?.scrollIntoView({
    behavior: 'smooth',
    block: 'center'
  })
})
</script>

<template>
  <VContainer
    class="success-page d-flex flex-column px-0"
    ref="successContainer"
  >
    <div
      data-elb="home"
      data-elbcontext="shopping:discover"
      class="d-flex py-4 py-md-10 justify-center w-100 px-3"
    >
      <RouterLink
        data-elbaction="click"
        to="/"
        class="logo-link w-100 d-flex align-center justify-center"
      >
        <img
          itemscope
          itemtype="https://schema.org/Brand"
          :src="logoMain"
          class="header-logo w-100 w-sm-50"
          alt="Vicampo"
        />
      </RouterLink>
    </div>

    <VRow>
      <VCol cols="12">
        <VSheet
          id="checkout-success"
          class="d-flex flex-column align-start w-100 position-relative py-5 py-md-10 px-5"
        >
          <div class="align-self-center" v-if="isLoading">
            <LoadingOverlay class="position-relative" width="75" height="75" />
          </div>
          <div
            v-else-if="isDataReady"
            ref="elbwalkerTarget"
            class="w-100"
            data-elb="order"
            data-elbcontext="shopping:complete"
            data-elbaction="load:complete"
            :data-elb-order="elbOrderData"
          >
            <SuccessHeader :data="successData?.order_increment_id" />

            <span
              v-if="!isUpsellActive && !isGuestOrder"
              class="credits text-center px-5 text-wrap d-block pt-10 pb-5"
              v-html="cashbackText"
            />

            <CheckoutUpsell
              v-if="isUpsellActive"
              :successData="successData"
              :productImage="productImage"
              :upsellImage="upsellImage"
              :placeholderImage="placeholderImage"
            />
            <FocusPlusCampaingBanner v-if="showFocusPlusCampaignBanner" />
            <span
              v-if="isUpsellActive && !isGuestOrder"
              class="credits text-center px-5 text-wrap d-block pt-10 pb-5"
              v-html="cashbackText"
            />
            <CheckoutRegisterCustomer
              v-else-if="canSetPassword"
              :email="profile?.email"
              :orderHash="hash"
            />

            <TrustedShopsCard :data="trustedShopData" />
          </div>
        </VSheet>
      </VCol>
    </VRow>
  </VContainer>
</template>

<style scoped>
.logo-link {
  max-width: 100%;
}
</style>
